import { mediaQueries } from '@tallink/components-lib/src/constants/media'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useMedia } from 'react-use'

import { getReservation, getReservationSerialTicketsGroupedByRoute } from '../../../selectors/selectors'
import Route from '../../common/Route'
import s from './SerialTicketsConfirmation.module.scss'

export default function SerialTicketsConfirmation() {
  const reservation = useSelector(getReservation)
  const groups = useSelector(getReservationSerialTicketsGroupedByRoute)

  return (
    <div className={s['serial-tickets-confirmation']}>
      {groups.map(({ from, to, tickets }) => (
        <div className={s['serial-ticket']} key={from + to}>
          <Route className={s.route} from={from} to={to} />
          <Header />
          <ConfirmationTable tickets={tickets} />
        </div>
      ))}
      <TotalPrice price={reservation.totalPrice} />
    </div>
  )
}

function Header() {
  const { t } = useTranslation()

  return (
    <div className={s.header}>
      <div className={s.label}>{t('price.description')}</div>
    </div>
  )
}

function TotalPrice({ price }) {
  const { t } = useTranslation()

  return (
    <div className={s['total-price']}>
      <div className={s.label}>{t('price.total')}:</div>
      <div className={s.price}>{price} €</div>
    </div>
  )
}

TotalPrice.propTypes = {
  price: PropTypes.number.isRequired
}

function ConfirmationTable({ tickets }) {
  return (
    <>
      <TableHeader />
      <TableBody tickets={tickets} />
    </>
  )
}

const ticket = PropTypes.shape({
  code: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  from: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  nameNarrow: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired,
  totalPrice: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  unitPrice: PropTypes.number.isRequired
})

ConfirmationTable.propTypes = {
  tickets: PropTypes.arrayOf(ticket).isRequired
}

function TableHeader() {
  const { t } = useTranslation()

  return (
    <div className={s['table-header']}>
      <div className={s['description-and-amount']}>
        <div className={s.description}>{t('price.description')}</div>
        <div className={s.amount}>{t('price.amount')}</div>
      </div>
      <div className={s['price-and-total']}>
        <div className={s.price}>{t('price.price')}</div>
        <div className={s.total}>{t('price.total')}</div>
      </div>
    </div>
  )
}

TicketDescriptionAndAmount.propTypes = {
  count: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  nameNarrow: PropTypes.string.isRequired
}

function TableBody({ tickets }) {
  const { t } = useTranslation()

  return (
    <div className={s['table-body']}>
      {tickets.map(({ count, name, nameNarrow, totalPrice, unitPrice }) => (
        <div className={s.row} key={name}>
          <TicketDescriptionAndAmount count={count} name={name} nameNarrow={nameNarrow} />
          <div className={s['price-and-total']}>
            <div className={s.price}>
              <div className={s.label}>{t('price.price')}</div>
              <div>{unitPrice} €</div>
            </div>
            <div className={s.total}>
              <div className={s.label}>{t('price.total')}</div>
              <div>{totalPrice} €</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

TableBody.propTypes = {
  tickets: PropTypes.arrayOf(ticket).isRequired
}

function TicketDescriptionAndAmount({ count, name, nameNarrow }) {
  const smUp = useMedia(mediaQueries.smUp)

  return (
    <div className={s['description-and-amount']}>
      <span className={s.description} dangerouslySetInnerHTML={{ __html: smUp ? name : nameNarrow }} />
      <span className={s.times}>×</span>
      <span className={s.amount}>{count}</span>
    </div>
  )
}

TicketDescriptionAndAmount.propTypes = {
  count: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  nameNarrow: PropTypes.string.isRequired
}

function PriceAndTotal({ unitPrice, totalPrice }) {
  const { t } = useTranslation()

  return (
    <div className={s['price-and-total']}>
      <div className={s.price}>
        <div className={s.label}>{t('price.price')}</div>
        <div>{unitPrice} €</div>
      </div>
      <div className={s.total}>
        <div className={s.label}>{t('price.total')}</div>
        <div>{totalPrice} €</div>
      </div>
    </div>
  )
}

PriceAndTotal.propTypes = {
  totalPrice: PropTypes.number.isRequired,
  unitPrice: PropTypes.number.isRequired
}
